import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../../environment";
import { handleAuthError, getAuthHeaders } from "../../../../utils/authUtils";
import { Link } from "react-router-dom";
import { DEFAULT_AVATAR } from "../../../../environment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { initialSettings } from "../../../../core/common/selectoption/selectoption";
import TaskDetailModal from "../../../../components/TaskDetailModal";
import Swal from "sweetalert2";

interface UserTask {
  task_id: number;
  title: string;
  category: string;
  start_date: string | null;
  due_date: string | null;
  priority: string;
  status: string;
  description: string;
  created_datetime: string | null;
  related_entity_name: string;
  related_entity_id: number;
  related_entity_title: string;
  responsible_persons: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  responsible_team: {
    team_id: number;
    name: string;
  };
  created_by: {
    user_id: number;
    full_name: string;
  };
  tenant_id: number;
  tenant: {
    tenant_id: number;
    tenant_name: string;
  };
}

interface UserTasksResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserTask[];
}

interface UserTasksProps {
  userId: string;
  tenantId: string;
}

const UserTasks: React.FC<UserTasksProps> = ({ userId, tenantId }) => {
  const [tasks, setTasks] = useState<UserTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({});
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState<any>(null);
  const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<UserTask | null>(null);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (e.currentTarget.src !== DEFAULT_AVATAR) {
      e.currentTarget.src = DEFAULT_AVATAR;
    }
  };

  const formatTaskDate = (dateString: string | null): string => {
    if (!dateString) return "Not set";

    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusBadgeColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-info";
      case "in progress":
        return "bg-warning";
      case "completed":
        return "bg-success";
      default:
        return "bg-secondary";
    }
  };

  const getPriorityBadgeColor = (priority: string): string => {
    switch (priority.toLowerCase()) {
      case "low":
        return "bg-success";
      case "medium":
        return "bg-warning";
      case "high":
        return "bg-danger";
      default:
        return "bg-info";
    }
  };

  const fetchUserTasks = async () => {
    if (!userId || !tenantId) return;

    setLoading(true);
    setError(null);

    try {
      let endpoint = `${API_BASE_URL}/task/?tenant_id=${tenantId}&responsible_persons=${userId}`;
      
      if (dateRange.start) {
        endpoint += `&due_date_gte=${encodeURIComponent(dateRange.start)}`;
      }
      if (dateRange.end) {
        endpoint += `&due_date_lte=${encodeURIComponent(dateRange.end)}`;
      }

      const response = await axios.get<UserTasksResponse>(endpoint, {
        headers: getAuthHeaders(),
      });

      setTasks(response.data.results);
    } catch (err: any) {
      handleAuthError(err);
      setError("Error loading tasks.");
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (start?: string, end?: string) => {
    if (!start || !end) {
      setDateRange({});
    } else {
      setDateRange({ start, end });
    }
  };

  const hasActiveFilters = () => {
    return dateRange.start !== undefined || dateRange.end !== undefined;
  };

  const clearAllFilters = () => {
    setDateRange({});
  };

  const createEntityUrl = (task: UserTask): string => {
    if (!task.related_entity_id) return "#";
    
    const entityName = task.related_entity_name.toLowerCase();
    switch (entityName) {
      case "lead":
        return `/leads/${task.related_entity_id}`;
      case "deal":
        return `/deals/${task.related_entity_id}`;
      case "contact":
        return `/contacts/${task.related_entity_id}`;
      case "company":
        return `/companies/${task.related_entity_id}`;
      case "pipeline":
        return `/pipelines/${task.related_entity_id}`;
      default:
        return `/${task.related_entity_name}/${task.related_entity_id}`;
    }
  };

  const fetchTaskDetails = async (taskId: number) => {
    if (!taskId) return;
    
    setIsLoadingTaskDetails(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/task/${taskId}/`, {
        headers: getAuthHeaders(),
      });

      if (response.data) {
        setSelectedTaskDetails(response.data);
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
    } finally {
      setIsLoadingTaskDetails(false);
    }
  };

  const handleTaskTitleClick = (task: UserTask) => {
    setTaskModalVisible(true);
    fetchTaskDetails(task.task_id);
  };

  const handleTaskDetailClick = (task: UserTask) => {
    setTaskModalVisible(true);
    fetchTaskDetails(task.task_id);
  };

  const handleTaskEditClick = (task: UserTask) => {
    setTaskModalVisible(true);
    fetchTaskDetails(task.task_id);
  };

  const handleTaskDeleteClick = (task: UserTask) => {
    setTaskToDelete(task);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/task/${taskToDelete.task_id}/`, {
        headers: getAuthHeaders(),
      });

      setShowDeleteConfirm(false);
      setTaskToDelete(null);
      
      Swal.fire({
        title: "Success!",
        text: "Task deleted successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });

      fetchUserTasks();
    } catch (err: any) {
      console.error("Error deleting task:", err);
      Swal.fire({
        title: "Error!",
        text: "Failed to delete task. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTaskModalClose = () => {
    setTaskModalVisible(false);
    setSelectedTaskDetails(null);
  };

  const handleTaskUpdate = () => {
    fetchUserTasks();
    if (selectedTaskDetails?.task_id) {
      fetchTaskDetails(selectedTaskDetails.task_id);
    }
  };

  useEffect(() => {
    fetchUserTasks();
  }, [userId, tenantId, dateRange]);

  const totalTasks = tasks.length;

  
  const today = new Date();
  const todayString = today.toISOString().split('T')[0];
  
  const todaysTasks = tasks.filter(task => {
    if (!task.due_date) return false;
    const taskDate = new Date(task.due_date);
    const taskDateString = taskDate.toISOString().split('T')[0];
    return taskDateString === todayString;
  });

  const expiredTasks = tasks.filter(task => {
    if (!task.due_date) return false;
    const taskDate = new Date(task.due_date);
    const taskDateString = taskDate.toISOString().split('T')[0];
    return taskDateString < todayString;
  });


  return (
    <div className="col-md-12 d-flex">
      <div className="card flex-fill">
        <div className="card-header">
          <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4 className="mb-0 d-flex align-items-center" style={{ fontSize: "1.25rem" }}>
              <span className="db-chart-icon">
                <i className="ti ti-checklist" />
              </span>
              My Recent Tasks
            </h4>
            <div className="d-flex align-items-center flex-wrap row-gap-2">
              {hasActiveFilters() && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm me-2"
                  onClick={clearAllFilters}
                >
                  <i className="ti ti-filter-x me-1" />
                  Clear
                </button>
              )}
              <div className="icon-form me-2" style={{ minWidth: '180px' }}>
                <span className="form-icon">
                  <i className="ti ti-calendar" />
                </span>
                <DateRangePicker
                  initialSettings={{
                    ...initialSettings,
                    startDate: dateRange.start
                      ? new Date(dateRange.start)
                      : new Date(),
                    endDate: dateRange.end
                      ? new Date(dateRange.end)
                      : (() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          return tomorrow;
                        })(),
                  }}
                  onApply={(event: any, picker: any) => {
                    const startDate = picker.startDate.format("YYYY-MM-DD");
                    const endDate = picker.endDate.format("YYYY-MM-DD");
                    handleDateRangeChange(startDate, endDate);
                  }}
                  onCancel={() => {
                    handleDateRangeChange();
                  }}
                >
                  <input
                    className="form-control bookingrange"
                    type="text"
                    placeholder="Select date range"
                    style={{ fontSize: '0.75rem' }}
                    value={
                      dateRange.start && dateRange.end
                        ? `${new Date(
                            dateRange.start
                          ).toLocaleDateString()} - ${new Date(
                            dateRange.end
                          ).toLocaleDateString()}`
                        : (() => {
                            const today = new Date();
                            const tomorrow = new Date();
                            tomorrow.setDate(today.getDate() + 1);
                            return `${today.toLocaleDateString()} - ${tomorrow.toLocaleDateString()} `;
                          })()
                    }
                    readOnly
                  />
                </DateRangePicker>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body" style={{ height: '300px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">
                <small className="text-muted">Loading tasks...</small>
              </div>
            </div>
          ) : error ? (
            <div className="text-center" style={{ padding: "60px 20px" }}>
              <i className="ti ti-alert-circle text-danger mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-danger">Error Loading Tasks</h5>
              <p className="text-muted">{error}</p>
            </div>
          ) : totalTasks === 0 ? (
            <div className="text-center" style={{ padding: "60px 20px" }}>
              <i className="ti ti-checklist text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">No Tasks Available</h5>
              <p className="text-muted">You don't have any tasks assigned to you at the moment.</p>
            </div>
          ) : (
            <div className="task-wrapper">
              {todaysTasks.length > 0 && (
                <div className="task-section mb-3">
                  <div className="badge badge-soft-success fs-14 fw-normal shadow-none mb-3">
                    <i className="ti ti-calendar-check me-1" />
                    Today's Tasks ({todaysTasks.length})
                  </div>
                  {todaysTasks.map((task) => {
                    const avatar = task.responsible_persons?.avatar_url || DEFAULT_AVATAR;
                    return (
                      <div key={task.task_id} className="card border shadow-none mb-2">
                        <div className="card-body p-3">
                          <div className="d-flex">
                            <span className="avatar avatar-md flex-shrink-0 rounded me-2 bg-success">
                              <i className="ti ti-checklist" />
                            </span>
                            <div className="flex-grow-1">
                              <p className="mb-1" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                You have a task <button 
                                  onClick={() => handleTaskTitleClick(task)}
                                  style={{ 
                                    background: 'none', 
                                    border: 'none', 
                                    padding: 0, 
                                    color: '#824eeb', 
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                  }}
                                >{task.title}</button> in {task.related_entity_name} 
                                {task.related_entity_title && <Link to={createEntityUrl(task)} target="_blank" rel="noopener noreferrer" className="mx-1"><b>{task.related_entity_title}</b></Link>}
                              </p>
                              <div className="d-flex gap-1 mb-1 flex-wrap">
                                <span className={`badge ${getPriorityBadgeColor(task.priority)}`} style={{ fontSize: '0.6rem' }}>
                                  {task.priority}
                                </span>
                                <span className={`badge ${getStatusBadgeColor(task.status)}`} style={{ fontSize: '0.6rem' }}>
                                  {task.status}
                                </span>
                              </div>
                              <p className="mb-0" style={{ fontSize: '0.65rem', color: '#666' }}>
                                Due: {formatTaskDate(task.due_date)}
                              </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {task.responsible_persons?.full_name && (
                                <img
                                  src={avatar}
                                  alt="img"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title={task.responsible_persons.full_name}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                  onError={handleImageError}
                                />
                              )}
                              <div className="dropdown">
                                <button
                                  className="btn btn-light btn-sm border-0"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  style={{ padding: '4px 8px' }}
                                >
                                  <i className="ti ti-dots-vertical" style={{ fontSize: '14px' }}></i>
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                  <li>
                                    <button 
                                      className="dropdown-item d-flex align-items-center"
                                      onClick={() => handleTaskDetailClick(task)}
                                    >
                                      <i className="ti ti-eye me-2"></i>
                                      Detail
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item d-flex align-items-center"
                                      onClick={() => handleTaskEditClick(task)}
                                    >
                                      <i className="ti ti-edit me-2"></i>
                                      Edit
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  <li>
                                    <button 
                                      className="dropdown-item d-flex align-items-center text-danger"
                                      onClick={() => handleTaskDeleteClick(task)}
                                    >
                                      <i className="ti ti-trash me-2"></i>
                                      Delete
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {expiredTasks.length > 0 && (
                <div className="task-section">
                  <div className="badge badge-soft-danger fs-14 fw-normal shadow-none mb-3">
                    <i className="ti ti-alert-triangle me-1" />
                    Overdue Tasks ({expiredTasks.length})
                  </div>
                  {expiredTasks.map((task) => {
                    const avatar = task.responsible_persons?.avatar_url || DEFAULT_AVATAR;
                    return (
                      <div key={task.task_id} className="card border shadow-none mb-2">
                        <div className="card-body p-3">
                          <div className="d-flex">
                            <span className="avatar avatar-md flex-shrink-0 rounded me-2 bg-danger">
                              <i className="ti ti-alert-triangle" />
                            </span>
                            <div className="flex-grow-1">
                              <p className="mb-1" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                You had a task <button 
                                  onClick={() => handleTaskTitleClick(task)}
                                  style={{ 
                                    background: 'none', 
                                    border: 'none', 
                                    padding: 0, 
                                    color: '#824eeb', 
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                  }}
                                >{task.title}</button> in {task.related_entity_name} 
                                {task.related_entity_title && <Link to={createEntityUrl(task)} target="_blank" rel="noopener noreferrer" className="mx-1"><b>{task.related_entity_title}</b></Link>}
                              </p>
                              <div className="d-flex gap-1 mb-1 flex-wrap">
                                <span className={`badge ${getPriorityBadgeColor(task.priority)}`} style={{ fontSize: '0.6rem' }}>
                                  {task.priority}
                                </span>
                                <span className={`badge ${getStatusBadgeColor(task.status)}`} style={{ fontSize: '0.6rem' }}>
                                  {task.status}
                                </span>
                              </div>
                              <p className="mb-0" style={{ fontSize: '0.65rem', color: '#d33' }}>
                                Due: {formatTaskDate(task.due_date)}
                              </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {task.responsible_persons?.full_name && (
                                <img
                                  src={avatar}
                                  alt="img"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title={task.responsible_persons.full_name}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                  onError={handleImageError}
                                />
                              )}
                              <div className="dropdown">
                                <button
                                  className="btn btn-light btn-sm border-0"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  style={{ padding: '4px 8px' }}
                                >
                                  <i className="ti ti-dots-vertical" style={{ fontSize: '14px' }}></i>
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                  <li>
                                    <button 
                                      className="dropdown-item d-flex align-items-center"
                                      onClick={() => handleTaskDetailClick(task)}
                                    >
                                      <i className="ti ti-eye me-2"></i>
                                      Detail
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item d-flex align-items-center"
                                      onClick={() => handleTaskEditClick(task)}
                                    >
                                      <i className="ti ti-edit me-2"></i>
                                      Edit
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  <li>
                                    <button 
                                      className="dropdown-item d-flex align-items-center text-danger"
                                      onClick={() => handleTaskDeleteClick(task)}
                                    >
                                      <i className="ti ti-trash me-2"></i>
                                      Delete
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <TaskDetailModal
        isVisible={taskModalVisible}
        taskDetails={selectedTaskDetails}
        isLoading={isLoadingTaskDetails}
        onClose={handleTaskModalClose}
        onTaskUpdate={handleTaskUpdate}
        tenantId={tenantId}
      />

      {showDeleteConfirm && (
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1051 }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <div className="text-center">
                    <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                      <i className="ti ti-trash-x fs-36 text-danger" />
                    </div>
                    <h4 className="mb-2">Delete Task?</h4>
                    <p className="mb-0">
                      Are you sure you want to delete task "{taskToDelete?.title}"? This action cannot be undone.
                    </p>
                    <div className="d-flex align-items-center justify-content-center mt-4">
                      <button
                        type="button"
                        className="btn btn-light me-2"
                        onClick={handleDeleteCancel}
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleDeleteConfirm}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          "Yes, Delete it"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div 
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
            onClick={handleDeleteCancel}
          />
        </>
      )}
    </div>
  );
};

export default UserTasks; 